import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Inbox as InboxIcon, CheckCheck, Archive, Search, Bell, Filter,
  AlertCircle, CheckCircle2, Megaphone, FileText, Wallet, Users,
  ClipboardList, CalendarClock, Ticket, UserPlus, Gauge, RefreshCw,
  ExternalLink, MessageSquare, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

/* ========================= EVENT → FRIENDLY LABEL + CATEGORY MAPPING ========================= */
const EVENT_META = {
  // Approvals / Leaves
  "leave.requested":       { label: "Leave approval needed",   cat: "approvals",     icon: ClipboardList, tone: "amber" },
  "leave.approved":        { label: "Your leave was approved", cat: "attendance",    icon: CheckCircle2,  tone: "green" },
  "leave.rejected":        { label: "Your leave was declined", cat: "attendance",    icon: AlertCircle,   tone: "red" },
  // Tasks
  "task.created":          { label: "New task created",        cat: "tasks",         icon: ClipboardList, tone: "indigo" },
  "task.assigned":         { label: "Task assigned to you",    cat: "tasks",         icon: ClipboardList, tone: "cyan" },
  "task.reassigned":       { label: "Task reassigned",         cat: "tasks",         icon: RefreshCw,     tone: "amber" },
  "task.status_changed":   { label: "Task status updated",     cat: "tasks",         icon: ClipboardList, tone: "slate" },
  "task.commented":        { label: "New comment on task",     cat: "tasks",         icon: MessageSquare, tone: "sky" },
  "task.due_date_changed": { label: "Task due date changed",   cat: "tasks",         icon: CalendarClock, tone: "amber" },
  "task.follower_added":   { label: "You're now watching a task", cat: "tasks",      icon: Users,         tone: "indigo" },
  "task.completed":        { label: "Task completed",          cat: "tasks",         icon: CheckCircle2,  tone: "green" },
  "task.mentioned":        { label: "You were mentioned",      cat: "tasks",         icon: MessageSquare, tone: "violet" },
  "task.due_today":        { label: "Task due today",          cat: "tasks",         icon: CalendarClock, tone: "amber" },
  "task.overdue":          { label: "Task overdue",            cat: "tasks",         icon: AlertCircle,   tone: "red" },
  // Meetings
  "meeting.invited":       { label: "You've been invited to a meeting", cat: "meetings", icon: CalendarClock, tone: "sky" },
  "meeting.cancelled":     { label: "Meeting cancelled",       cat: "meetings",      icon: AlertCircle,   tone: "red" },
  "meeting.reminder":      { label: "Meeting starts soon",     cat: "meetings",      icon: CalendarClock, tone: "amber" },
  // Helpdesk / Claims
  "ticket.created":        { label: "New helpdesk ticket",     cat: "helpdesk",      icon: Ticket,        tone: "sky" },
  "ticket.assigned":       { label: "Ticket assigned to you",  cat: "helpdesk",      icon: Ticket,        tone: "cyan" },
  "ticket.updated":        { label: "Ticket update",           cat: "helpdesk",      icon: Ticket,        tone: "slate" },
  "claim.submitted":       { label: "Claim awaits approval",   cat: "approvals",     icon: Wallet,        tone: "amber" },
  "claim.approved":        { label: "Your claim was approved", cat: "helpdesk",      icon: CheckCircle2,  tone: "green" },
  "claim.rejected":        { label: "Your claim was declined", cat: "helpdesk",      icon: AlertCircle,   tone: "red" },
  // Announcements
  "announcement.published": { label: "New announcement",       cat: "announcements", icon: Megaphone,     tone: "violet" },
  // Payroll / Documents
  "payroll.payslip_ready": { label: "Payslip available",       cat: "payroll",       icon: Wallet,        tone: "teal" },
  "document.shared":       { label: "New document shared",     cat: "payroll",       icon: FileText,      tone: "slate" },
  // Recruitment / Goals / Lifecycle
  "recruitment.offer":     { label: "Offer letter sent",       cat: "system",        icon: UserPlus,      tone: "violet" },
  "goal.updated":          { label: "Goal updated",            cat: "system",        icon: Gauge,         tone: "indigo" },
  "lifecycle.onboarding_start": { label: "Onboarding started", cat: "system",        icon: UserPlus,      tone: "green" },
  "lifecycle.offboarding_start": { label: "Offboarding started", cat: "system",      icon: UserPlus,      tone: "red" },
};

const TONE_STYLES = {
  slate:  { pill: "bg-slate-100 text-slate-700", iconWrap: "bg-slate-100 text-slate-700", ring: "ring-slate-200", dot: "bg-slate-500" },
  cyan:   { pill: "bg-cyan-100 text-cyan-700",   iconWrap: "bg-cyan-100 text-cyan-700",   ring: "ring-cyan-200",  dot: "bg-cyan-500" },
  green:  { pill: "bg-emerald-100 text-emerald-700", iconWrap: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  amber:  { pill: "bg-amber-100 text-amber-700", iconWrap: "bg-amber-100 text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500" },
  red:    { pill: "bg-red-100 text-red-700",     iconWrap: "bg-red-100 text-red-700",     ring: "ring-red-200",   dot: "bg-red-500" },
  violet: { pill: "bg-violet-100 text-violet-700", iconWrap: "bg-violet-100 text-violet-700", ring: "ring-violet-200", dot: "bg-violet-500" },
  indigo: { pill: "bg-indigo-100 text-indigo-700", iconWrap: "bg-indigo-100 text-indigo-700", ring: "ring-indigo-200", dot: "bg-indigo-500" },
  sky:    { pill: "bg-sky-100 text-sky-700",     iconWrap: "bg-sky-100 text-sky-700",     ring: "ring-sky-200",   dot: "bg-sky-500" },
  teal:   { pill: "bg-teal-100 text-teal-700",   iconWrap: "bg-teal-100 text-teal-700",   ring: "ring-teal-200",  dot: "bg-teal-500" },
};

const ACTION_REQUIRED = new Set(["leave.requested", "claim.submitted", "task.assigned", "task.reassigned", "ticket.assigned", "task.overdue"]);
const HIGH_PRIORITY   = new Set(["task.overdue", "meeting.reminder", "leave.requested", "ticket.assigned"]);

/* ========================= HELPERS ========================= */
function fmtAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}
function dayBucket(iso) {
  const d = new Date(iso), now = new Date();
  const same = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (same) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  if ((now - d) / 86400000 < 7) return "This week";
  return "Earlier";
}
function meta(type) {
  return EVENT_META[type] || { label: type.replace(/_/g, " ").replace(/\./g, " · "), cat: "system", icon: Bell, tone: "slate" };
}

/* ========================= MAIN ========================= */
const CATEGORIES = [
  { key: "all",            label: "All",             icon: InboxIcon },
  { key: "unread",         label: "Unread",          icon: Bell },
  { key: "action",         label: "Action required", icon: AlertCircle },
  { key: "approvals",      label: "Approvals",       icon: ClipboardList },
  { key: "tasks",          label: "Tasks",           icon: ClipboardList },
  { key: "meetings",       label: "Meetings",        icon: CalendarClock },
  { key: "attendance",     label: "Leave",           icon: CheckCircle2 },
  { key: "helpdesk",       label: "Helpdesk",        icon: Ticket },
  { key: "announcements",  label: "Announcements",   icon: Megaphone },
  { key: "payroll",        label: "Payroll",         icon: Wallet },
  { key: "system",         label: "System",          icon: Users },
];

export default function Inbox() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sp] = useSearchParams();

  useEffect(() => { if (sp.get("category")) setCat(sp.get("category")); }, [sp]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/inbox", { params: { limit: 300 } }),
        api.get("/inbox/summary"),
      ]);
      setItems(r.data); setSummary(s.data);
    } catch (e) { toast.error("Failed to load inbox"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try { await api.post(`/inbox/${id}/read`); } catch {}
  };
  const archive = async (id) => {
    setItems(prev => prev.filter(n => n.id !== id));
    try { await api.post(`/inbox/${id}/archive`); toast.success("Archived"); load(); } catch { toast.error("Failed"); }
  };
  const markAllRead = async () => {
    try { await api.post("/inbox/mark-all-read"); toast.success("All marked as read"); load(); } catch { toast.error("Failed"); }
  };
  const archiveAllRead = async () => {
    if (!window.confirm("Archive all already-read notifications?")) return;
    try { const r = await api.post("/inbox/archive-all-read"); toast.success(`Archived ${r.data?.modified || 0}`); load(); } catch { toast.error("Failed"); }
  };

  /* ----- filtering ----- */
  const filtered = useMemo(() => {
    let out = items.slice();
    if (cat === "unread") out = out.filter(n => !n.read);
    else if (cat === "action") out = out.filter(n => ACTION_REQUIRED.has(n.type) && !n.read);
    else if (cat !== "all") out = out.filter(n => meta(n.type).cat === cat);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(n => (n.title || "").toLowerCase().includes(q) || (n.body || "").toLowerCase().includes(q));
    }
    return out;
  }, [items, cat, search]);

  /* ----- grouping by dayBucket + then by type (for dedupe) ----- */
  const grouped = useMemo(() => {
    const buckets = { "Today": [], "Yesterday": [], "This week": [], "Earlier": [] };
    filtered.forEach(n => {
      const b = dayBucket(n.created_at);
      (buckets[b] || buckets["Earlier"]).push(n);
    });
    // Within each bucket, group by (type + meta.id) so "3 new comments on task X" collapse
    const collapseBucket = (arr) => {
      const groups = {};
      arr.forEach(n => {
        const key = `${n.type}::${n.meta?.id || "-"}`;
        (groups[key] = groups[key] || []).push(n);
      });
      return Object.values(groups).map(g => g.length === 1 ? g[0] : { ...g[0], __group: g, __count: g.length });
    };
    return Object.entries(buckets).map(([b, arr]) => [b, collapseBucket(arr)]).filter(([_, arr]) => arr.length > 0);
  }, [filtered]);

  /* ----- quick review strip ----- */
  const quickStats = [
    { key: "action", label: "Action required", value: summary?.action_required || 0, tone: "amber", icon: AlertCircle },
    { key: "unread", label: "Unread",          value: summary?.total_unread || 0,    tone: "cyan",   icon: Bell },
    { key: "today",  label: "Today",           value: summary?.today_count || 0,     tone: "indigo", icon: CalendarClock },
    { key: "high",   label: "High priority",   value: summary?.priority_high || 0,   tone: "red",    icon: Gauge },
  ];

  const unreadInCat = (k) => {
    if (k === "all") return summary?.total_unread || 0;
    if (k === "unread") return summary?.total_unread || 0;
    if (k === "action") return summary?.action_required || 0;
    return (summary?.by_category || {})[k] || 0;
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="inbox-page">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-3 anim-fade-up">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Your Inbox</div>
          <h1 className="mt-1 text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-600 mt-1">Quick-scan and act on everything that needs your attention.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={markAllRead} data-testid="mark-all-read"><CheckCheck className="w-4 h-4 mr-1.5"/>Mark all read</Button>
          <Button variant="outline" size="sm" className="h-9" onClick={archiveAllRead} data-testid="archive-all-read"><Archive className="w-4 h-4 mr-1.5"/>Archive read</Button>
          <Button variant="ghost" size="sm" className="h-9" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></Button>
        </div>
      </div>

      {/* Quick review strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 anim-fade-up anim-delay-1">
        {quickStats.map(s => {
          const t = TONE_STYLES[s.tone];
          const Icon = s.icon;
          return (
            <Card key={s.key} className={`p-3.5 border-white/80 shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_20px_-14px_rgba(15,23,42,0.14)] ring-1 ${t.ring} bg-gradient-to-br from-white to-slate-50/60`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg ${t.iconWrap} flex items-center justify-center`}><Icon className="w-4 h-4"/></div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">{s.label}</div>
                  <div className="text-2xl font-[Outfit] font-semibold text-slate-900 leading-none mt-0.5 tabular-nums">{s.value}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Category tabs + search */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={cat} onValueChange={setCat} className="min-w-0 max-w-full">
          <TabsList className="bg-slate-100/80 h-11 p-1 gap-0.5 rounded-xl overflow-x-auto no-scrollbar flex-nowrap max-w-[calc(100vw-380px)] lg:max-w-none">
            {CATEGORIES.map(c => {
              const n = unreadInCat(c.key);
              const active = cat === c.key;
              return (
                <TabsTrigger
                  key={c.key}
                  value={c.key}
                  data-testid={`cat-${c.key}`}
                  className="h-9 px-3 shrink-0 whitespace-nowrap rounded-lg text-[13px] font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 hover:text-slate-900 transition-colors flex items-center"
                >
                  <c.icon className="w-3.5 h-3.5 mr-1.5 shrink-0"/>
                  <span>{c.label}</span>
                  {n > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center h-4 min-w-[18px] px-1 rounded-full text-[10px] font-semibold ${active ? "bg-cyan-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                      {n > 99 ? "99+" : n}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400"/>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search notifications…" className="pl-8 h-9 w-56 bg-white" data-testid="inbox-search"/>
        </div>
      </div>

      {/* Notification groups */}
      {loading && items.length === 0 ? (
        <Card className="p-16 text-center text-slate-500">Loading…</Card>
      ) : grouped.length === 0 ? (
        <Card className="p-16 text-center text-slate-500">
          <InboxIcon className="w-12 h-12 mx-auto text-slate-300"/>
          <div className="mt-3 font-medium text-slate-700">All caught up</div>
          <div className="text-xs mt-1">No notifications match this filter.</div>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(([bucket, rows]) => (
            <div key={bucket}>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{bucket}</div>
                <div className="flex-1 h-px bg-slate-200"/>
                <div className="text-[10px] text-slate-400">{rows.length}</div>
              </div>
              <div className="space-y-2">
                {rows.map(n => <NotificationCard key={n.id} n={n} onRead={markRead} onArchive={archive}/>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========================= CARD ========================= */
function NotificationCard({ n, onRead, onArchive }) {
  const m = meta(n.type);
  const t = TONE_STYLES[m.tone] || TONE_STYLES.slate;
  const Icon = m.icon;
  const count = n.__count || 0;
  const isGroup = count > 1;
  const important = ACTION_REQUIRED.has(n.type);
  const high = HIGH_PRIORITY.has(n.type);

  return (
    <div
      className={`group relative p-3.5 rounded-xl border border-slate-200 bg-white hover:shadow-[0_4px_16px_-4px_rgba(15,23,42,0.08)] transition-all ${!n.read ? "ring-1 ring-cyan-200/70" : ""}`}
      data-testid={`notif-${n.id}`}
    >
      {!n.read && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-500"/>}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${t.iconWrap} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${t.pill}`}>{m.label}</span>
            {isGroup && <Badge className="bg-slate-900 text-white border-0 text-[10px]">{count} updates</Badge>}
            {important && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Action</Badge>}
            {high && <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">High priority</Badge>}
            {!n.read && !important && !high && <Badge className="bg-cyan-50 text-cyan-700 border-cyan-100 text-[10px]">New</Badge>}
            <span className="ml-auto text-[10px] text-slate-400">{fmtAgo(n.created_at)}</span>
          </div>
          <div className="mt-1 text-sm font-medium text-slate-900 line-clamp-1">{n.title || "Notification"}</div>
          {n.body && <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">{n.body}</div>}
          {n.actor_name && <div className="mt-0.5 text-[11px] text-slate-500">by {n.actor_name}</div>}

          {/* Quick actions */}
          <div className="mt-2 flex items-center gap-1.5 opacity-80 group-hover:opacity-100 transition">
            {n.link && <Link to={n.link} onClick={() => onRead(n.id)}><Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" data-testid={`open-${n.id}`}><ExternalLink className="w-3 h-3 mr-1"/>Open</Button></Link>}
            {!n.read && <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => onRead(n.id)} data-testid={`read-${n.id}`}><CheckCheck className="w-3 h-3 mr-1"/>Mark read</Button>}
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-700" onClick={() => onArchive(n.id)} data-testid={`archive-${n.id}`}><Archive className="w-3 h-3 mr-1"/>Archive</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
